// Foundations
import { NgModule } from '@angular/core';
import { HttpClientModule } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { filter, flatMap } from 'rxjs/operators';
import { Observable } from 'rxjs/internal/Observable';

// Apollo
import { APOLLO_OPTIONS } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { InMemoryCache, ApolloLink, DefaultOptions } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';

// Services
import { CognitoService, getAccessToken } from '@app/services/cognito.service';
import { GraphQLErrorUtilsService } from './services/utils/graphql-error-utils.service';

// ENV
import { environment } from '@environments/environment';
import { ParsedGraphQLError } from './models/utils/graphql-error.model';

const uri = environment.gqlUrl;

export function createApollo(
  httpLink: HttpLink,
  matSnackBar: MatSnackBar,
  cognitoService: CognitoService,
  graphQLErrorUtilsService: GraphQLErrorUtilsService
) {
  const snackBar = matSnackBar;
  const cognitoServiceRef = cognitoService;
  const graphQLErrorUtilsServiceRef = graphQLErrorUtilsService;

  const basic = setContext((operation, context) => ({
    headers: {
      Accept: 'charset=utf-8',
    },
  }));

  const auth = setContext((operation, context) => {
    const token = getAccessToken();
    if (token === null) {
      return {};
    } else {
      return {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
    }
  });

  const error = onError(
    // @ts-ignore
    ({ graphQLErrors, networkError, operation, forward }) => {
      //
      if (graphQLErrors) {
        for (const err of graphQLErrors) {
          switch (err.extensions.code) {
            case 401: // "No token could be found."
              // return forward(operation);
              return cognitoServiceRef.isSessionValid().pipe(
                filter((isValid) => !!isValid),
                flatMap((isValid) => {
                  // Modify the operation context with a new token
                  const oldHeaders = operation.getContext().headers;
                  const newToken = getAccessToken();
                  operation.setContext({
                    headers: {
                      ...oldHeaders,
                      Authorization: `Bearer ${newToken}`,
                    },
                  });

                  // Retry the requests
                  return forward(operation);
                })
              );
            default:
              // Try to parse the error first for a better message
              const parsedError: ParsedGraphQLError =
                graphQLErrorUtilsServiceRef.parseGraphQLError(err);
              snackBar?.open(
                'GraphQL Error: ' + parsedError.outputMessage,
                '',
                {
                  duration: 2000,
                }
              );

              // Check if need to report this error
              graphQLErrorUtilsServiceRef.reportGraphQLErrorIfNeeded(
                parsedError,
                operation
              );

              // Return as an error so that it can be captured by `catchError`.
              return new Observable(function (observer) {
                observer.error(parsedError);
              });
          }
        }
      }

      //
      if (networkError) {
        snackBar?.open('Network Error' + networkError.message, '', {
          duration: 2000,
        });

        // Check if need to report this error
        graphQLErrorUtilsServiceRef.reportGraphQLErrorIfNeeded(
          networkError,
          operation
        );
      }
    }
  );

  const link = ApolloLink.from([basic, auth, error, httpLink.create({ uri })]);
  const cache = new InMemoryCache();
  const defaultOptions: DefaultOptions = {
    // For diff. `fetchPolicy`.
    // Ref - https://medium.com/@galen.corey/understanding-apollo-fetch-policies-705b5ad71980
    watchQuery: {
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    },
    query: {
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    },
  };

  return {
    link,
    cache,
    defaultOptions,
  };
}

@NgModule({
  exports: [HttpClientModule],
  providers: [
    {
      provide: APOLLO_OPTIONS,
      useFactory: createApollo,
      deps: [HttpLink, MatSnackBar, CognitoService, GraphQLErrorUtilsService],
    },
  ],
})
export class GraphQLModule {}
